// Settings → File Storage: S3 connection, the one-time copy of older files and
// the nightly database backups.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, DatabaseBackup, Loader2, RefreshCw, XCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const mb = (b) => (b == null ? "—" : b > 1e9 ? `${(b / 1e9).toFixed(2)} GB` : `${(b / 1e6).toFixed(1)} MB`);
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function Row({ label, children, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-800">{children}</span>
    </div>
  );
}

function CopyCard({ m, canEdit, onRetry }) {
  const c = m.counts || {};
  const done = (c.copied || 0) + (c.already || 0) + (c.missing || 0) + (c.failed || 0);
  return (
    <Card className="border-slate-200 p-5 shadow-sm" data-testid="s3-copy-card">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Copy of existing files</h3>
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium capitalize text-slate-600" data-testid="s3-copy-state">{m.state}</span>
      </div>
      <Row label="Progress" testid="s3-copy-progress">{c.total ? `${done.toLocaleString()} of ${c.total.toLocaleString()}` : "—"}</Row>
      <Row label="Copied to S3">{(c.copied || 0).toLocaleString()} ({mb(c.bytes)})</Row>
      <Row label="Original already missing">{(c.missing || 0).toLocaleString()}</Row>
      <Row label="Failed (retried automatically)" testid="s3-copy-failed">{(c.failed || 0).toLocaleString()}</Row>
      <Row label="Finished">{when(m.finished_at)}</Row>
      {canEdit && m.state === "incomplete" && (
        <Button size="sm" variant="outline" className="mt-2" onClick={onRetry} data-testid="s3-copy-retry">
          <RefreshCw className="h-4 w-4" /> Retry failed files now
        </Button>
      )}
    </Card>
  );
}

function BackupCard({ b, list, keep, canEdit, onRun }) {
  return (
    <Card className="border-slate-200 p-5 shadow-sm" data-testid="s3-backup-card">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Nightly database backup (1:30 am, keeps {keep})</h3>
        {canEdit && (
          <Button size="sm" variant="outline" disabled={b.state === "running"} onClick={onRun} data-testid="s3-backup-now">
            {b.state === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />} Back up now
          </Button>
        )}
      </div>
      <Row label="Last run" testid="s3-backup-state">{b.state}{b.finished_at ? ` · ${when(b.finished_at)}` : ""}</Row>
      {b.error && <p className="text-xs text-red-600" data-testid="s3-backup-error">{b.error}</p>}
      <div className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-100" data-testid="s3-backup-list">
        {list.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No backups yet.</p>}
        {list.map((d) => (
          <div key={d.day} className="flex justify-between px-3 py-1.5 text-xs" data-testid={`s3-backup-${d.day}`}>
            <span className="font-medium text-slate-700">{d.day}</span>
            <span className="text-slate-500">{d.complete ? `${d.collections} collections · ${mb(d.bytes)}` : "in progress / incomplete"}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function S3StoragePanel({ canEdit }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/storage/s3").then((r) => setData(r.data)).catch(() => setData({ error: true })), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const post = (url, msg) => api.post(url).then(() => { toast.success(msg); load(); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  if (!data) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>;
  if (data.error || !data.configured) return <Card className="p-6 text-sm text-slate-500" data-testid="s3-not-configured">S3 storage isn&apos;t configured in this environment.</Card>;
  return (
    <div className="space-y-4" data-testid="s3-storage-panel">
      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          {data.connection.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
          <h3 className="text-sm font-semibold text-slate-800" data-testid="s3-connection">{data.connection.ok ? "Connected to Amazon S3" : "S3 not reachable — uploads will fail"}</h3>
        </div>
        {!data.connection.ok && <p className="text-xs text-red-600">{data.connection.error}</p>}
        <Row label="Bucket" testid="s3-bucket">{data.bucket}</Row>
        <Row label="Region">{data.region}</Row>
        <Row label="This environment's folder" testid="s3-folder">{data.folder}/</Row>
      </Card>
      <CopyCard m={data.migration} canEdit={canEdit} onRetry={() => post("/admin/storage/s3/copy-retry", "Retrying failed files")} />
      <BackupCard b={data.backup} list={data.backups} keep={data.keep_days} canEdit={canEdit}
        onRun={() => post("/admin/storage/s3/backup-now", "Backup started")} />
    </div>
  );
}
